"use client";
import { rem } from "../../core/utils/units-converters/rem.mjs";
import { getFontSize, getRadius, getSize } from "../../core/utils/get-size/get-size.mjs";
import { createVarsResolver } from "../../core/styles-api/create-vars-resolver/create-vars-resolver.mjs";
import { useProps } from "../../core/MantineProvider/use-props/use-props.mjs";
import { useStyles } from "../../core/styles-api/use-styles/use-styles.mjs";
import { polymorphicFactory } from "../../core/factory/polymorphic-factory.mjs";
import { Box } from "../../core/Box/Box.mjs";
import { UnstyledButton } from "../UnstyledButton/UnstyledButton.mjs";
import { Transition } from "../Transition/Transition.mjs";
import { Loader } from "../Loader/Loader.mjs";
import Button_module_default from "./Button.module.mjs";
import { ButtonGroup } from "./ButtonGroup/ButtonGroup.mjs";
import { ButtonGroupSection } from "./ButtonGroupSection/ButtonGroupSection.mjs";
import { jsx, jsxs } from "react/jsx-runtime";
//#region packages/@mantine/core/src/components/Button/Button.tsx
const loaderTransition = {
	in: {
		opacity: 1,
		transform: `translate(-50%, calc(-50% + ${rem(1)}))`
	},
	out: {
		opacity: 0,
		transform: "translate(-50%, -200%)"
	},
	common: { transformOrigin: "center" },
	transitionProperty: "transform, opacity"
};
const varsResolver = createVarsResolver((theme, { radius, color, gradient, variant, size, justify, autoContrast }) => {
	const colors = theme.variantColorResolver({
		color: color || theme.primaryColor,
		theme,
		gradient,
		variant: variant || "filled",
		autoContrast
	});
	return { root: {
		"--button-justify": justify,
		"--button-height": getSize(size, "button-height"),
		"--button-padding-x": getSize(size, "button-padding-x"),
		"--button-fz": size?.includes("compact") ? getFontSize(size.replace("compact-", "")) : getFontSize(size),
		"--button-radius": radius === void 0 ? void 0 : getRadius(radius),
		"--button-bg": color || variant ? colors.background : void 0,
		"--button-hover": color || variant ? colors.hover : void 0,
		"--button-color": colors.color,
		"--button-bd": color || variant ? colors.border : void 0,
		"--button-hover-color": color || variant ? colors.hoverColor : void 0
	} };
});
const Button = polymorphicFactory((_props) => {
	const props = useProps("Button", null, _props);
	const { style, vars, className, color, disabled, children, leftSection, rightSection, fullWidth, variant, radius, loading, loaderProps, gradient, classNames, styles, unstyled, "data-disabled": dataDisabled, autoContrast, mod, attributes, ...others } = props;
	const getStyles = useStyles({
		name: "Button",
		props,
		classes: Button_module_default,
		className,
		style,
		classNames,
		styles,
		unstyled,
		attributes,
		vars,
		varsResolver
	});
	const hasLeftSection = !!leftSection;
	const hasRightSection = !!rightSection;
	return /* @__PURE__ */ jsxs(UnstyledButton, {
		...getStyles("root", { active: !disabled && !loading && !dataDisabled }),
		unstyled,
		variant,
		disabled: disabled || loading,
		mod: [{
			disabled: disabled || dataDisabled,
			loading,
			block: fullWidth,
			"with-left-section": hasLeftSection,
			"with-right-section": hasRightSection
		}, mod],
		...others,
		children: [typeof loading === "boolean" && /* @__PURE__ */ jsx(Transition, {
			mounted: loading,
			transition: loaderTransition,
			duration: 150,
			children: (transitionStyles) => /* @__PURE__ */ jsx(Box, {
				component: "span",
				...getStyles("loader", { style: transitionStyles }),
				"aria-hidden": true,
				children: /* @__PURE__ */ jsx(Loader, {
					color: "var(--button-color)",
					size: "calc(var(--button-height) / 1.8)",
					...loaderProps
				})
			})
		}), /* @__PURE__ */ jsxs("span", {
			...getStyles("inner"),
			children: [
				leftSection && /* @__PURE__ */ jsx(Box, {
					component: "span",
					...getStyles("section"),
					mod: { position: "left" },
					children: leftSection
				}),
				/* @__PURE__ */ jsx(Box, {
					component: "span",
					mod: { loading },
					...getStyles("label"),
					children
				}),
				rightSection && /* @__PURE__ */ jsx(Box, {
					component: "span",
					...getStyles("section"),
					mod: { position: "right" },
					children: rightSection
				})
			]
		})]
	});
});
Button.classes = Button_module_default;
Button.varsResolver = varsResolver;
Button.displayName = "@mantine/core/Button";
Button.Group = ButtonGroup;
Button.GroupSection = ButtonGroupSection;
//#endregion
export { Button };

//# sourceMappingURL=Button.mjs.map